import { useCallback } from 'react';

import { Search as SearchIcon } from '@mui/icons-material';
import { Box, Button, Divider, Paper, TextField, Typography } from '@mui/material';
import type { GridPaginationModel } from '@mui/x-data-grid';
import ExcelJS from 'exceljs';
import { useSetAtom } from 'jotai';
import { MLDataGrid } from 'ml-react-component-library';

import type {
  ParsedEngagementRecord,
  ParsedIManageDocument,
} from '@/apps/engagement-letter-database/hooks/useParseRecords';
import { PermissionGate, usePermission } from '@/core/auth/permissions';
import { buildIManageUrl } from '@/shared/helpers/imanageUrlUtils';
import { openModalAtom } from '@/shared/state/atoms';

import { createColumns } from '../../constants/index';

import CustomToolbar from './CustomToolbar';

interface SearchResultsProps {
  rows: ParsedIManageDocument[];
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  loading?: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  recordInfo: ParsedEngagementRecord;
}

const SearchResults = ({
  rows,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  loading,
  searchTerm,
  onSearchChange,
  recordInfo,
}: SearchResultsProps) => {
  const openModal = useSetAtom(openModalAtom);
  const { hasPermission } = usePermission();
  const canEdit = hasPermission('EngagementRecords', 'update');
  // Map the parsed document to the DocumentForm's field shape before opening the modal.
  const handleEdit = (row: ParsedIManageDocument) => {
    openModal({ mode: 'edit', data: { id: row.id, documentName: row.docName ?? '', iManageDocNumber: row.docId } });
  };
  const handleView = (row: ParsedIManageDocument) => {
    openModal({ mode: 'view', data: { id: row.id, documentName: row.docName ?? '', iManageDocNumber: row.docId } });
  };
  const handleAdd = () => {
    openModal({ mode: 'add', data: null });
  };

  const columns = createColumns({
    onEdit: handleEdit,
    onView: handleView,
    canEdit,
  });

  // Exports the current document dataset to an .xlsx file.
  const handleExport = useCallback(async () => {
    const workbook = new ExcelJS.Workbook();
    const documentsWorksheet = workbook.addWorksheet('Engagement Letter Documents');
    const clientInfoWorksheet = workbook.addWorksheet('Client Info');
    const commentsWorksheet = workbook.addWorksheet('Comments');
    commentsWorksheet.columns = [
      { header: 'Comment', key: 'comment', width: 50 },
      { header: 'Created By', key: 'createdBy', width: 25 },
      { header: 'Created Date', key: 'createdDate', width: 20 },
    ];
    commentsWorksheet.getRow(1).font = { bold: true };
    clientInfoWorksheet.columns = [
      { header: 'Client Name', key: 'clientName', width: 30 },
      { header: 'Client Number', key: 'clientNumber', width: 20 },
      { header: 'Responsible Attorney', key: 'responsibleAttorney', width: 30 },
      { header: 'Practice Group', key: 'practice', width: 20 },
      { header: 'Open Date', key: 'openDate', width: 15 },
      { header: 'Closed Date', key: 'closedDate', width: 15 },
      { header: 'Created By', key: 'createdBy', width: 25 },
      { header: 'Created Date', key: 'createdDate', width: 20 },
      { header: 'Modified By', key: 'modifiedBy', width: 25 },
      { header: 'Modified Date', key: 'modifiedDate', width: 20 },
    ];
    clientInfoWorksheet.getRow(1).font = { bold: true };
    clientInfoWorksheet.addRow({
      clientName: recordInfo.clientName,
      clientNumber: recordInfo.clientNumber,
      responsibleAttorney: recordInfo.responsibleAttorney,
      practice: recordInfo.practiceGroup,
      openDate: recordInfo.clientOpenDate,
      closedDate: recordInfo.clientClosedDate,
      createdBy: recordInfo.createdBy,
      createdDate: recordInfo.createdAt,
      modifiedBy: recordInfo.updatedBy,
      modifiedDate: recordInfo.updatedAt,
    });
    documentsWorksheet.columns = [
      { header: 'Document Name', key: 'documentName', width: 40 },
      { header: 'iManage Doc Number', key: 'iManageDocNumber', width: 24 },
      { header: 'iManage Link', key: 'iManageDocLink', width: 50 },
    ];
    documentsWorksheet.getRow(1).font = { bold: true };

    rows.forEach((row) => {
      documentsWorksheet.addRow({
        documentName: row.docName ?? '',
        iManageDocNumber: row.docId,
        iManageDocLink: buildIManageUrl(row.docId),
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const namePart =
      recordInfo.clientName
        ?.trim()
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '') || 'engagement-documents';
    link.download = `${namePart}-${new Date().toISOString().slice(0, 10)}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  }, [rows, recordInfo]);

  return (
    <Paper elevation={2} sx={{ p: 3, marginBottom: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Engagement Letter Documents
          </Typography>
        </Box>
        <Divider />
        <Box
          sx={{
            paddingTop: 2,
            paddingBottom: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
          }}
        >
          <TextField
            sx={{
              width: 325,
              maxWidth: '100%',
            }}
            label="Search Documents"
            placeholder="Search by document name or doc number..."
            size="small"
            variant="outlined"
            margin="none"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', marginRight: 1 }}>
                    <SearchIcon />
                  </Box>
                ),
              },
            }}
          />
          <PermissionGate resource="EngagementRecords" action="create">
            <Button variant="outlined" size="small" onClick={handleAdd}>
              Add Document
            </Button>
          </PermissionGate>
        </Box>
      </Box>
      <MLDataGrid
        showToolbar
        slots={{
          toolbar: CustomToolbar,
        }}
        slotProps={{
          toolbar: { onExport: () => void handleExport() },
        }}
        rows={rows}
        columns={columns}
        paginationMode="server"
        pagination
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[25, 50, 100]}
        loading={loading}
      />
    </Paper>
  );
};

export default SearchResults;
